import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Input, Divider, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import LaundryCell from './LaundryCell';
import './LaundrySection.scss';
import garmentIcon from 'assets/images/garment.png';
import householdIcon from 'assets/images/household.png';
import totalIcon from 'assets/images/tatal.png';
import { fetchCollectionItems } from 'services/orderService';
import { CollectionItems, OrderDetailItem } from 'types/dto/order';
import linkImage from 'assets/images/link.svg';
// import { useSession } from 'context/SessionContext';
import { SecurityUtils } from 'utils/securityUtils';


const { TextArea } = Input;
interface LaundrySectionProps {
  isReadonly?: boolean;
  showQuantityError?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onLoadError?: (error: boolean) => void;
}

const getIconByType = (itemTypeCode: string) => {
  switch (itemTypeCode) {
  case 'GAR':
  case 'GAR_D':
  case 'GAR_L':
    return (
      <img
        src={garmentIcon}
        alt="Garment"
        width="92"
        height="86"
        style={{ objectFit: 'contain' }}
      />
    );
  case 'HHD':
  case 'HOU_D':
  case 'HOU_L':
    return (
      <img
        src={householdIcon}
        alt="Household"
        width="92"
        height="86"
        style={{ objectFit: 'contain' }}
      />
    );
  default:
    return (
      <img
        src={garmentIcon}
        alt="Default"
        width="92"
        height="86"
        style={{ objectFit: 'contain' }}
      />
    );
  }
};

const LaundrySection: React.FC<LaundrySectionProps> = ({
  isReadonly = false,
  showQuantityError = false,
  onLoadingChange,
  onLoadError,
}) => {
  const { t, i18n } = useTranslation('new_order');
  const lang = i18n.language;
  const form = Form.useFormInstance();
  // const {session} = useSession();

  // Use state to store collection items for rendering UI
  const [collectionItems, setCollectionItems] = useState<CollectionItems[]>([]);

  // Watch form values for total calculation
  const formOrderDetailItems: OrderDetailItem[] =
    Form.useWatch('orderDetailsItems', form) || [];
  const totalCount = formOrderDetailItems.reduce(
    (total: number, item: OrderDetailItem) => total + (item.qty || 0),
    0
  );

  useEffect(() => {
    const initData = async () => {
      try {
        onLoadingChange?.(true);
        onLoadError?.(false);

        const responseData = await fetchCollectionItems('/customer', lang);
        // Set to state for rendering UI
        setCollectionItems(responseData);

        // Transform CollectionItems to OrderDetailItem for form submission
        const orderDetailItems: OrderDetailItem[] = responseData.map((item: CollectionItems) => ({
          itemTypeCode: item.itemTypeCode,
          qty: item.qty,
          seq: item.seq,
        }));

        // Set to form with the transformed data
        form.setFieldValue('orderDetailsItems', orderDetailItems);

        onLoadingChange?.(false);
      } catch (err) {
        console.error('Failed to obtain the service type:', err);
        onLoadError?.(true);
        onLoadingChange?.(false);
      }
    };
    initData();
  }, [lang, form, onLoadingChange, onLoadError]);

  const TotalCell = () => (
    <Col xs={24} sm={8}>
      <LaundryCell
        label={t('total') || 'Total'}
        value={totalCount}
        disabled={true}
        isReadonly={isReadonly}
        placeholder="0"
        max={4}
        icon={
          <img
            src={totalIcon}
            alt="Total"
            width="120"
            height="86"
            style={{ objectFit: 'contain' }}
          />
        }
      />
    </Col>
  );

  return (
    <div className="laundry-section">
      <span className="vls-section-title">{t('laundryDetails')}</span>

      {totalCount === 0 && !isReadonly && showQuantityError && (
        <div className="error-message">
          {t('pleaseEnterQuantity')}
        </div>
      )}

      <Row gutter={[48, 24]} className="laundry-cells-row">
        {collectionItems.map((item, index) => (
          <Col xs={24} sm={8} key={item.itemId}>
            <Form.Item
              name={['orderDetailsItems', index]}
              getValueFromEvent={(value) => {
                // When LaundryCell onChange is called with a number,
                // merge it with the existing item data
                const currentItem =
                  form.getFieldValue(['orderDetailsItems', index]) || {};
                return {
                  ...currentItem,
                  qty: value || 0,
                };
              }}
              getValueProps={(itemData) => {
                // Extract qty from the item object to pass to LaundryCell
                return { value: itemData?.qty || 0 };
              }}
            >
              <LaundryCell
                label={t(`${item.itemTypeName}(pcs)`) || item.itemTypeName}
                placeholder="0"
                max={3}
                icon={getIconByType(item.itemTypeCode)}
                showTooltip={item.itemTypeCode.startsWith('HOU')}
                tooltipText={
                  item.itemTypeCode.startsWith('HOU')
                    ? t('householdTooltip') || 'Household items information'
                    : ''
                }
                isReadonly={isReadonly}
              />
            </Form.Item>
          </Col>
        ))}

        <TotalCell />
      </Row>

      <div className="laundry-info">
        <span className="quantity-note">
          *{t('totalPcsNote')}
          <Button
            type='link'
            className="check-prices-link"
            onClick={() => {
              const currentLang = i18n.language.startsWith('zh') ? 'hk' : 'en';
              const targetUrl = `https://www.voguelaundry.com/${currentLang}/pickup_price_list.html`;
              window.open(targetUrl, '_blank');
            }}
          >
            {t('checkPrices')} <img src={linkImage} />
          </Button>
        </span>

        <span>{t('minimumCharge')}</span>
      </div>

      <Divider className="divider" />

      <div className="remark-section">
        <Form.Item
          name="remark"
          label={t('remark') || 'Remark'}
          className="remark-form-item"
          getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value)}
        >
          {isReadonly ? (
            <div className="readonly-remark">
              {form.getFieldValue('remark') || '-'}
            </div>
          ) : (
            <TextArea
              placeholder={t('remarkPlaceholder')}
              rows={4}
              className="remark-textarea"
            />
          )}
        </Form.Item>
      </div>
    </div>
  );
};

export default LaundrySection;
